import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  faArrowDownToLine,
  faPrint,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import {
  faChevronLeft,
  faImages,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Toast } from '@repo/videa-components/src/index';
import { Button } from '@repo/videa-components/src/ui/button';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Redirect, useLocation, useParams } from 'react-router-dom';
import { Toaster, toast } from 'sonner';

import { LoadingAnimation } from '@/common-components/animations/loading/LoadingAnimation';
import { BackLinkSubheader } from '@/common-components/elements/BackLinkSubheader/BackLinkSubheader';
import { FeedbackWidget } from '@/common-components/elements/FeedbackWidget/FeedbackWidget';
import { useGetDateFNSLocale } from '@/locales/helpers';
import { claimAttachmentIdAtom } from '@/modules/claims/freeClaims/atoms';
import {
  PageProvider,
  usePageContext,
} from '@/modules/claims/freeClaims/context/PageContext';
import { useClaimSummary } from '@/modules/claims/freeClaims/hooks/useClaimSummary';
import { useTreatmentsToUse } from '@/modules/claims/freeClaims/hooks/useTreatmentsToUse';
import { useShouldHideHuddle } from '@/modules/patientList/useShouldHideHuddle';
import type { ClaimSummaryGenerateRequest } from '@/react-query/claimSummaryGenerate';
import { useHasClaimAttachmentWritebackSupport } from '@/react-query/rcm/useHasClaimAttachmentWritebackSupport';
import {
  type InsuranceNarrativeResult,
  type InsuranceNarrativeResultV3,
  useGetNarrativeWithPreference,
} from '@/react-query/rcm/useNarrative';
import { useDownloadPieces } from '@/react-query/useDownloadPieces';
import { useGeneratePdf } from '@/react-query/useGeneratePdf';
import { usePreferences } from '@/react-query/usePreferences';
import { usePushToPMS } from '@/react-query/usePushToPMS';
import {
  useWritebackStatus,
  writebackStatusKeys,
} from '@/react-query/useWritebackStatus';
import routesConstants from '@/routes/routes.constants';
import { useLatest } from '@videa/utils';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ZoomToolTopbar } from '../../../../../common-components/elements/ZoomToolTopbar.tsx/ZoomToolTopbar';
import {
  claimAttachmentFmxImagesAtom,
  claimAttachmentFmxTableDataAtom,
  claimAttachmentToothLevelImagesAtom,
  claimNotesAtom,
  claimWarningsAtom,
  cleanClaimsAttachmentValuesAtom,
  clinicalNotesAtom,
  excludeFmxFindingsWhenPeriochartPresentAtom,
  includeClinicalNoteInAttachmentAtom,
  insPagesAtom,
  isNarrativeLoadingAtom,
  narrativeAtom,
  narrativeDevToolDataAtom,
  narrativeTraceIdAtom,
  selectedImageKeysAtom,
  selectedTreatmentCodesAtom,
  selectedTreatmentsAtom,
  showAnnotationsAtom,
  showPerioChartAtom,
} from '../../../freeClaims/state';
import { batchSessionAtom, setBatchResult } from '../../batchSession';
import {
  createLocalDate,
  filterImagesByTreatments,
  generateFmxTitle,
  getClaimNotesAndWarnings,
  getEnabledAttachmentViews,
  normalizeEstimatedVisitId,
  sortImagesByDisplayItem,
  transformFmxImagesForPdf,
  transformToothLevelImagesForPdf,
} from '../../helpers';

import { BatchHeader } from './BatchHeader';
import { ClaimAttachmentContent } from './claimAttachment/ClaimAttachmentContent';
import { ClaimCustomizationSidebar } from './claimCustomization/ClaimCustomizationSidebar';
import { NarrativeDevTool } from './NarrativeDevTool';
import { PushStatusLabel } from './PushStatusLabel';
import { ClaimImageSelector } from './selectImagesForClaim/ClaimImageSelectorModal';

const RATE_ATTACHMENT_OPTIONS_UP = [
  {
    value: 'accurate-information',
    label: <Trans>Accurate information</Trans>,
  },
  {
    value: 'minimal-edits',
    label: <Trans>Minimal edits needed</Trans>,
  },
  {
    value: 'saved-time',
    label: <Trans>Saved time</Trans>,
  },
  {
    value: 'correct-terminology',
    label: <Trans>Correct terminology</Trans>,
  },
  {
    value: 'correct-images',
    label: <Trans>Correct images selected</Trans>,
  },
];

const RATE_ATTACHMENT_OPTIONS_DOWN = [
  {
    value: 'missing-treatments',
    label: <Trans>Missing treatments</Trans>,
  },
  {
    value: 'incorrect-codes',
    label: <Trans>Incorrect codes</Trans>,
  },
  {
    value: 'mislabeled-images',
    label: <Trans>Mislabeled images</Trans>,
  },
  {
    value: 'incorrect-info',
    label: <Trans>Incorrect patient or provider information</Trans>,
  },
];

const TOAST_TOP_RIGHT = {
  closeButton: true,
  position: 'top-right' as const,
};
const TOAST_TOP_RIGHT_WIDE = {
  ...TOAST_TOP_RIGHT,
  style: { width: '460px' },
};

// Create a wrapper component that provides the context
const ClaimSummaryPageWrapper = () => {
  return (
    <PageProvider>
      <ClaimSummaryPage />
    </PageProvider>
  );
};

const ClaimSummaryPage = () => {
  // Page corresponding to a claim summary for a patient and visit
  // refs
  const contentRef = useRef<HTMLDivElement>(null);
  /** True when a push was initiated this session and a toast hasn't been shown yet */
  const hasPushPendingRef = useRef<boolean>(false);
  const pushTreatmentCodesRef = useRef<string[]>([]);

  // URL: path has unifiedId; query has estimatedVisitId and imagingPatientId (imaging patient videa id; supports refresh / deep link)
  const params = useParams<{ unifiedId: string }>();
  const location = useLocation();
  const unifiedIdParam = params.unifiedId;
  const { estimatedVisitIdFromUrl, imagingPatientIdFromUrl } = useMemo(() => {
    const search = new URLSearchParams(location.search);
    return {
      estimatedVisitIdFromUrl: search.get('estimatedVisitId') ?? undefined,
      imagingPatientIdFromUrl: search.get('imagingPatientId') ?? undefined,
    };
  }, [location.search]);

  const cleanClaimsAttachmentValues = useAtomValue(
    cleanClaimsAttachmentValuesAtom,
  );

  // batch session (state)
  const [batchSession, setBatchSession] = useAtom(batchSessionAtom);

  // atoms
  const [insPage, setInsPage] = useAtom(insPagesAtom);
  const [selectedTreatmentIds, setSelectedTreatmentIds] = useAtom(
    selectedTreatmentsAtom,
  );
  const setCleanClaimsAttachmentValues = useSetAtom(
    cleanClaimsAttachmentValuesAtom,
  );

  const selectedTreatmentCodes = useAtomValue(selectedTreatmentCodesAtom);
  const shouldExcludeFmxFindingsWhenPeriochartPresent = useAtomValue(
    excludeFmxFindingsWhenPeriochartPresentAtom,
  );

  const setNarrative = useSetAtom(narrativeAtom);
  const setNarrativeDevToolData = useSetAtom(narrativeDevToolDataAtom);
  const setNarrativeTraceId = useSetAtom(narrativeTraceIdAtom);
  const setIsNarrativeLoading = useSetAtom(isNarrativeLoadingAtom);
  const setClaimAttachmentId = useSetAtom(claimAttachmentIdAtom);
  const setSelectedTreatmentCodes = useSetAtom(selectedTreatmentCodesAtom);
  const setClaimNotes = useSetAtom(claimNotesAtom);
  const setClaimWarnings = useSetAtom(claimWarningsAtom);
  const setClinicalNotes = useSetAtom(clinicalNotesAtom);
  const setShowAnnotations = useSetAtom(showAnnotationsAtom);
  const setShowPerioChart = useSetAtom(showPerioChartAtom);

  const [showFMX, setShowFMX] = useState(true);
  const [selectedImageKeys, setSelectedImageKeys] = useAtom(
    selectedImageKeysAtom,
  );
  const setClaimAttachmentToothLevelImages = useSetAtom(
    claimAttachmentToothLevelImagesAtom,
  );
  const setClaimAttachmentFmxImages = useSetAtom(claimAttachmentFmxImagesAtom);
  const setClaimAttachmentFmxTableData = useSetAtom(
    claimAttachmentFmxTableDataAtom,
  );
  const setExcludeFmxFindingsWhenPeriochartPresent = useSetAtom(
    excludeFmxFindingsWhenPeriochartPresentAtom,
  );
  const setIncludeClinicalNoteInAttachment = useSetAtom(
    includeClinicalNoteInAttachmentAtom,
  );

  // state
  const [scale, setScale] = useState(1);
  const [contentHeight, setContentHeight] = useState(0);
  const [hasInitiallyScaled, setHasInitiallyScaled] = useState(false);
  /** Incremented on each push to restart the 130s timeout effect */
  const [pushSessionId, setPushSessionId] = useState(0);
  const [isImageSelectorOpen, setIsImageSelectorOpen] = useState(false);

  // Reset zoom state whenever the claim identity changes so every claim opens at "fit to page"
  useEffect(() => {
    setScale(1);
    setHasInitiallyScaled(false);
  }, [unifiedIdParam]);

  // hooks
  const queryClient = useQueryClient();
  const locale = useGetDateFNSLocale();

  // Hydrate atom from URL (findings provide patientInfo via the claimSummary effect below)
  useEffect(() => {
    setCleanClaimsAttachmentValues({
      unifiedId: unifiedIdParam,
      estimatedVisitId: estimatedVisitIdFromUrl,
      locale,
    });
  }, [
    unifiedIdParam,
    estimatedVisitIdFromUrl,
    locale,
    setCleanClaimsAttachmentValues,
  ]);

  const unifiedId = unifiedIdParam;
  const estimatedVisitId = normalizeEstimatedVisitId(estimatedVisitIdFromUrl);
  /** Always holds the current claim's unifiedId — used to detect stale push callbacks after navigation */
  const currentUnifiedIdRef = useLatest(unifiedId);

  // hooks (must all run before any conditional return to satisfy rules of hooks)
  const { data: preferences } = usePreferences();
  const { t } = useLingui();
  const { componentRef } = usePageContext();
  const imagingPatientVideaId = imagingPatientIdFromUrl;
  const { claimSummary, isLoadingClaimSummary } = useClaimSummary({
    estimatedVisitId,
    imagingPatientVideaId,
    unifiedAppointmentId: parseInt(unifiedId, 10),
  });

  useEffect(() => {
    const hasFindingsPatientInfo =
      claimSummary?.patientName != null ||
      claimSummary?.providerName != null ||
      claimSummary?.dateOfService != null;
    if (!hasFindingsPatientInfo || !claimSummary) {
      return;
    }
    setCleanClaimsAttachmentValues({
      unifiedId: unifiedIdParam,
      estimatedVisitId: estimatedVisitIdFromUrl,
      patientInfo: {
        name: claimSummary.patientName ?? '',
        dob: claimSummary.patientDateOfBirth ?? '',
        dateOfVisit: claimSummary.dateOfService ?? '',
        providerName: claimSummary.providerName ?? '',
      },
      locale,
    });
  }, [
    claimSummary,
    unifiedIdParam,
    estimatedVisitIdFromUrl,
    locale,
    setCleanClaimsAttachmentValues,
  ]);

  useEffect(() => {
    if (
      claimSummary?.clinicalNote != null &&
      claimSummary.clinicalNote.trim() !== ''
    ) {
      setClinicalNotes(claimSummary.clinicalNote);
    }
  }, [claimSummary?.clinicalNote, setClinicalNotes]);

  const { trackEvent } = useTrackEvent();
  const hideHuddle = useShouldHideHuddle();
  const isClaimAttachmentEnabled = preferences?.claimAttachmentEnabled;
  const hasClaimAttachmentWritebackSupport =
    useHasClaimAttachmentWritebackSupport().data ?? false;
  const shouldShowPushToPMS = hasClaimAttachmentWritebackSupport;

  const handleAttachmentButtonClick = useCallback(
    (rating: 'up' | 'down') => {
      // Only track up ratings immediately - down ratings will be tracked when feedback is submitted
      if (rating === 'up') {
        trackEvent(mixpanelEvents.rateClaimAttachment, {
          txCode: selectedTreatmentCodes,
          source: 'attachment',
          rating,
        });
      }
    },
    [trackEvent, selectedTreatmentCodes],
  );

  const handleSubmitAttachmentFeedback = useCallback(
    (
      rating: 'up' | 'down',
      selectedOptions: string[],
      additionalComments?: string,
    ) => {
      // TODO BOSON-319: Send rating to UserPilot

      // Track down feedback when submitted (up feedback was already tracked on button click)
      if (rating === 'down') {
        trackEvent(mixpanelEvents.rateClaimAttachment, {
          txCode: selectedTreatmentCodes,
          source: 'attachment',
          rating,
          reason: selectedOptions.join(', ') || undefined,
          comment: additionalComments || undefined,
        });
      }
    },
    [trackEvent, selectedTreatmentCodes],
  );

  const handleBackLinkClick = useCallback(() => {
    if (selectedTreatmentCodes?.length) {
      trackEvent(mixpanelEvents.closeAttachmentEditor, {
        unifiedId,
        estimatedVisitId,
        entryPoint: 'Top link',
        txCode: selectedTreatmentCodes,
      });
    }
  }, [selectedTreatmentCodes, trackEvent, unifiedId, estimatedVisitId]);

  // Use preference to determine which narrative endpoint to call (v2 or v3)
  const isLlmNarrativeEnabled = preferences?.llmClaimNarrativeEnabled ?? false;

  // eslint-disable-next-line react-hooks/exhaustive-deps -- dependency is selectedTreatmentIds
  const narrativeTreatmentIds = useMemo(
    () =>
      (selectedTreatmentIds ?? [])
        .map((tx) => Number(tx))
        .filter(Number.isFinite),
    [selectedTreatmentIds],
  );

  const {
    data: narrativeData,
    isLoading: isLoadingNarrative,
    isFetching: isFetchingNarrative,
  } = useGetNarrativeWithPreference(
    narrativeTreatmentIds,
    parseInt(unifiedId, 10),
    estimatedVisitId,
    imagingPatientVideaId,
    isLlmNarrativeEnabled,
    {
      onError() {
        setNarrative('None provided.');
        setNarrativeDevToolData(undefined);
        setNarrativeTraceId(undefined);
        setIsNarrativeLoading(false);
      },
      onSuccess(data: InsuranceNarrativeResult | InsuranceNarrativeResultV3) {
        setNarrative(data.narrative);
        // V2 has devToolData, V3 doesn't
        setNarrativeDevToolData(
          'devToolData' in data ? data.devToolData : undefined,
        );
        setNarrativeTraceId(data.traceId ?? undefined);
        setIsNarrativeLoading(false);
      },
      retry: true,
      enabled: (narrativeTreatmentIds?.length ?? 0) > 0,
    },
  );

  // Mutations and writeback status
  const {
    mutate: generatePdf,
    isLoading: isPdfLoading,
    reset: resetPdfMutation,
  } = useGeneratePdf();
  const {
    mutate: pushToPMS,
    isLoading: isPushingToPMS,
    reset: resetPushToPMSMutation,
  } = usePushToPMS();
  const {
    mutate: downloadPieces,
    isLoading: isDownloadingPieces,
    reset: resetDownloadPiecesMutation,
  } = useDownloadPieces();
  const { data: writebackStatus } = useWritebackStatus(unifiedId);
  const images = useAtomValue(claimAttachmentToothLevelImagesAtom);
  const fmxImages = useAtomValue(claimAttachmentFmxImagesAtom);
  const fmxTableData = useAtomValue(claimAttachmentFmxTableDataAtom);
  const clinicalNote = useAtomValue(clinicalNotesAtom);
  const shouldIncludeClinicalNoteInAttachment = useAtomValue(
    includeClinicalNoteInAttachmentAtom,
  );
  const narrative = useAtomValue(narrativeAtom);
  const attachmentID = useAtomValue(claimAttachmentIdAtom);

  // Filter/sort images for PDF
  const { colorImages: filteredColorImages, radiographs: filteredRadiographs } =
    useMemo(() => {
      const filteredImages = filterImagesByTreatments(
        claimSummary?.images ?? [],
        claimSummary?.imagesDisplay ?? [],
        selectedTreatmentIds,
      );

      const colorImages = filteredImages?.filter(
        (img) => img.imageType === 'COLOR_IMAGE',
      );

      const radiographs = filteredImages?.filter(
        (img) => img.imageType !== 'COLOR_IMAGE',
      );

      return {
        colorImages: sortImagesByDisplayItem(
          colorImages,
          claimSummary?.imagesDisplay ?? [],
        ),
        radiographs: sortImagesByDisplayItem(
          radiographs,
          claimSummary?.imagesDisplay ?? [],
        ),
      };
    }, [
      claimSummary?.images,
      claimSummary?.imagesDisplay,
      selectedTreatmentIds,
    ]);

  // Apply manual image selection (if any) to determine which images are visible
  const { visibleColorImages, visibleRadiographs } = useMemo(() => {
    if (selectedImageKeys === null) {
      // Default: only best intraoral when a *visible* (filtered) display item has intraoralOrder 0
      const filteredColorImageIds = new Set(
        filteredColorImages?.map((img) => img.imageId) ?? [],
      );
      const displayItemsForVisibleImages =
        claimSummary?.imagesDisplay?.filter((item) =>
          filteredColorImageIds.has(item.id),
        ) ?? [];
      const defaultBestIntraoralOnly = displayItemsForVisibleImages.some(
        (item) =>
          item.intraoralOrder !== undefined && item.intraoralOrder === 0,
      );
      return {
        visibleColorImages:
          defaultBestIntraoralOnly && filteredColorImages?.length
            ? filteredColorImages.slice(0, 1)
            : (filteredColorImages ?? []),
        visibleRadiographs: filteredRadiographs ?? [],
      };
    }

    const keySet = new Set(selectedImageKeys);

    return {
      visibleColorImages:
        filteredColorImages?.filter((_, index) =>
          keySet.has(`color-${index}`),
        ) ?? [],
      visibleRadiographs:
        filteredRadiographs?.filter((_, index) =>
          keySet.has(`radiograph-${index}`),
        ) ?? [],
    };
  }, [
    claimSummary?.imagesDisplay,
    filteredColorImages,
    filteredRadiographs,
    selectedImageKeys,
  ]);

  // Generate FMX title
  const fmxTitle = useMemo(() => {
    return generateFmxTitle(claimSummary?.treatments, selectedTreatmentIds);
  }, [claimSummary?.treatments, selectedTreatmentIds]);

  // variables
  const { treatments: claimSummaryTreatments = [] } = claimSummary ?? {};

  const { treatmentsToUse } = useTreatmentsToUse({
    allTreatments: claimSummaryTreatments ?? [],
    selectedTreatmentIds: selectedTreatmentIds ?? [],
  });

  const { hasToothLevelSummary, hasQuadLevelSummary } =
    getEnabledAttachmentViews({
      selectedTreatmentIds,
      claimSummary,
    });

  const isActionsDisabled =
    isLoadingClaimSummary || isLoadingNarrative || isFetchingNarrative;

  const loadingMessages = [
    <Trans>Selecting high quality radiographs...</Trans>,
    <Trans>Extracting clinical findings...</Trans>,
    <Trans>Crafting narrative...</Trans>,
  ];

  const handleSelectTreatments = useCallback(
    (txIds: string[]) => {
      setSelectedTreatmentIds(txIds);
      setSelectedTreatmentCodes([
        ...new Set(
          treatmentsToUse
            .filter((tx) => txIds.includes(tx.txId.toString()))
            .map((tx) => tx.txCode),
        ),
      ]);
      setShowFMX(true);
      setSelectedImageKeys(null);
    },
    [
      setSelectedTreatmentIds,
      setSelectedTreatmentCodes,
      treatmentsToUse,
      setShowFMX,
      setSelectedImageKeys,
    ],
  );

  // Transform data for PDF generation
  const transformedToothLevelImagesForPdf = useMemo(() => {
    return transformToothLevelImagesForPdf(images);
  }, [images]);

  const transformedFmxImagesForPdf = useMemo(() => {
    if (!showFMX) {
      return [];
    }
    return transformFmxImagesForPdf(
      claimSummary,
      selectedTreatmentIds,
      fmxImages,
    );
  }, [claimSummary, selectedTreatmentIds, fmxImages, showFMX]);

  // Builds the push-to-PMS / PDF / download payload from the current atom state.
  const buildClaimSummaryGenerateRequest =
    useCallback((): ClaimSummaryGenerateRequest => {
      return {
        unifiedAppointmentId: unifiedId,
        attachmentID,
        narrative,
        includeClinicalNoteInAttachment: shouldIncludeClinicalNoteInAttachment,
        clinicalNote: shouldIncludeClinicalNoteInAttachment
          ? clinicalNote
          : undefined,
        fmxTitle,
        toothLevelImages: transformedToothLevelImagesForPdf,
        fmxImages: transformedFmxImagesForPdf,
        fmxTableData:
          transformedFmxImagesForPdf && transformedFmxImagesForPdf.length > 0
            ? fmxTableData
            : undefined,
        includeFmxFindings: !shouldExcludeFmxFindingsWhenPeriochartPresent,
      };
    }, [
      unifiedId,
      attachmentID,
      narrative,
      clinicalNote,
      shouldIncludeClinicalNoteInAttachment,
      fmxTitle,
      transformedToothLevelImagesForPdf,
      transformedFmxImagesForPdf,
      fmxTableData,
      shouldExcludeFmxFindingsWhenPeriochartPresent,
    ]);

  // PDF generation handler
  const handleGeneratePdf = useCallback(() => {
    if (batchSession != null) {
      setBatchSession((prev) => setBatchResult(prev, unifiedId, 'attempted'));
    }

    trackEvent(mixpanelEvents.generatePdf, {
      unifiedId,
      estimatedVisitId,
      txCode: selectedTreatmentCodes,
    });

    // Show warning toast about potential wait time and store its ID
    const warningToastId = toast(
      <Toast
        variant="warning"
        title={t`Generating PDF...`}
        description={t`This may take up to a minute. Please wait while we process your request.`}
      />,
      TOAST_TOP_RIGHT_WIDE,
    );

    generatePdf(buildClaimSummaryGenerateRequest(), {
      onSuccess: () => {
        // Dismiss the warning toast
        toast.dismiss(warningToastId);

        toast(
          <Toast
            variant="success"
            title={t`PDF generated successfully!`}
            description={t`Your claim attachment PDF has been downloaded.`}
          />,
          TOAST_TOP_RIGHT_WIDE,
        );
        // Reset the mutation state to ensure button text resets
        resetPdfMutation();
      },
      onError: (error) => {
        console.error('Failed to generate PDF:', error);

        // Dismiss the warning toast
        toast.dismiss(warningToastId);

        toast(
          <Toast
            variant="error"
            title={t`PDF generation failed`}
            description={t`Failed to generate PDF. Please try again or contact support if the problem persists.`}
          />,
          TOAST_TOP_RIGHT_WIDE,
        );
        // Reset the mutation state to ensure button text resets
        resetPdfMutation();
      },
    });
  }, [
    batchSession,
    setBatchSession,
    buildClaimSummaryGenerateRequest,
    generatePdf,
    resetPdfMutation,
    t,
    trackEvent,
    unifiedId,
    estimatedVisitId,
    selectedTreatmentCodes,
  ]);

  // Download button handler - downloads ZIP of PNG pieces from backend
  const handleDownloadPieces = useCallback(() => {
    if (batchSession != null) {
      setBatchSession((prev) => setBatchResult(prev, unifiedId, 'attempted'));
    }

    if (selectedTreatmentCodes.length) {
      trackEvent(mixpanelEvents.downloadAttachment, {
        unifiedId,
        estimatedVisitId,
        txCode: selectedTreatmentCodes,
      });
    }

    downloadPieces(
      {
        ...buildClaimSummaryGenerateRequest(),
        patientName: cleanClaimsAttachmentValues.patientInfo?.name,
      },
      {
        onSuccess: () => {
          toast(
            <Toast
              variant="success"
              title={t`Success`}
              description={t`ZIP downloaded successfully`}
            />,
            TOAST_TOP_RIGHT,
          );
          resetDownloadPiecesMutation();
        },
        onError: (error) => {
          console.error('Error downloading pieces:', error);
          toast(
            <Toast
              variant="error"
              title={t`Error`}
              description={t`Failed to download. Please try again.`}
            />,
            TOAST_TOP_RIGHT,
          );
          resetDownloadPiecesMutation();
        },
      },
    );
  }, [
    batchSession,
    setBatchSession,
    selectedTreatmentCodes,
    trackEvent,
    unifiedId,
    estimatedVisitId,
    downloadPieces,
    resetDownloadPiecesMutation,
    buildClaimSummaryGenerateRequest,
    cleanClaimsAttachmentValues.patientInfo?.name,
    t,
  ]);

  // Push to PMS button handler - initiates push and starts status polling
  const handlePushToPMS = useCallback(() => {
    if (batchSession != null) {
      setBatchSession((prev) => setBatchResult(prev, unifiedId, 'attempted'));
    }

    // Capture treatment codes for analytics when polling completes
    pushTreatmentCodesRef.current = selectedTreatmentCodes;

    // Track push started
    trackEvent(mixpanelEvents.pushToPMSStarted, {
      unifiedId,
      estimatedVisitId,
      txCode: selectedTreatmentCodes,
    });

    pushToPMS(buildClaimSummaryGenerateRequest(), {
      onSuccess: () => {
        // Guard: the user may have navigated to a different claim before the HTTP
        // response arrived. If so, discard this callback — acting on it would set
        // hasPushPendingRef to true for the wrong claim and trigger false toasts.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- useLatest ref is stable
        if (String(unifiedId) !== String(currentUnifiedIdRef.current)) return;
        queryClient.setQueryData(
          writebackStatusKeys.detail(String(unifiedId)),
          {
            status: 'pending',
            documents: [],
          },
        );
        hasPushPendingRef.current = true;
        setPushSessionId((n) => n + 1);
        resetPushToPMSMutation();
      },
      onError: (_error) => {
        hasPushPendingRef.current = false;
        toast(
          <Toast
            variant="error"
            title={t`Error`}
            description={t`Failed to push to PMS. Please try again.`}
          />,
          TOAST_TOP_RIGHT,
        );
        resetPushToPMSMutation();
      },
    });
  }, [
    pushToPMS,
    buildClaimSummaryGenerateRequest,
    queryClient,
    trackEvent,
    unifiedId,
    estimatedVisitId,
    selectedTreatmentCodes,
    resetPushToPMSMutation,
    t,
    batchSession,
    setBatchSession,
    currentUnifiedIdRef,
  ]);

  // useEffects
  // Reset all claim-specific state on param change (body) and on unmount (cleanup).
  useEffect(() => {
    setNarrative('');
    setClaimNotes([]);
    setClaimWarnings([]);
    setClinicalNotes('');
    setIsNarrativeLoading(true);
    setSelectedTreatmentIds([]);
    setSelectedTreatmentCodes([]);
    setNarrativeDevToolData(undefined);
    setNarrativeTraceId(undefined);
    setClaimAttachmentId(undefined);
    setClaimAttachmentToothLevelImages([]);
    setClaimAttachmentFmxImages([]);
    setClaimAttachmentFmxTableData([]);
    setExcludeFmxFindingsWhenPeriochartPresent(false);
    setIncludeClinicalNoteInAttachment(false);
    setSelectedImageKeys(null);
    setShowAnnotations(true);
    setShowPerioChart(true);
    resetPushToPMSMutation();
    resetDownloadPiecesMutation();
    resetPdfMutation();
    hasPushPendingRef.current = false;

    return () => {
      setNarrative('');
      setClaimNotes([]);
      setClaimWarnings([]);
      setClinicalNotes('');
      setIsNarrativeLoading(false);
      setSelectedTreatmentIds([]);
      setSelectedTreatmentCodes([]);
      setNarrativeDevToolData(undefined);
      setNarrativeTraceId(undefined);
      setClaimAttachmentId(undefined);
      setClaimAttachmentToothLevelImages([]);
      setClaimAttachmentFmxImages([]);
      setClaimAttachmentFmxTableData([]);
      setExcludeFmxFindingsWhenPeriochartPresent(false);
      setIncludeClinicalNoteInAttachment(false);
      setSelectedImageKeys(null);
      setShowAnnotations(true);
      setShowPerioChart(true);
      resetPushToPMSMutation();
      resetDownloadPiecesMutation();
      resetPdfMutation();
      hasPushPendingRef.current = false;
    };
  }, [
    unifiedId,
    estimatedVisitId,
    setNarrative,
    setClaimNotes,
    setClaimWarnings,
    setClinicalNotes,
    setIsNarrativeLoading,
    setSelectedTreatmentIds,
    setSelectedTreatmentCodes,
    setNarrativeDevToolData,
    setNarrativeTraceId,
    setClaimAttachmentId,
    setClaimAttachmentToothLevelImages,
    setClaimAttachmentFmxImages,
    setClaimAttachmentFmxTableData,
    setExcludeFmxFindingsWhenPeriochartPresent,
    setIncludeClinicalNoteInAttachment,
    setSelectedImageKeys,
    setShowAnnotations,
    setShowPerioChart,
    resetPushToPMSMutation,
    resetDownloadPiecesMutation,
    resetPdfMutation,
  ]);

  // Handle writeback status polling results — only show toast if a push was initiated this session
  useEffect(() => {
    if (!hasPushPendingRef.current || isPushingToPMS) return;

    if (writebackStatus?.status === 'success') {
      hasPushPendingRef.current = false;
      toast(
        <Toast
          variant="success"
          title={t`Success`}
          description={t`Successfully pushed to PMS`}
        />,
        TOAST_TOP_RIGHT,
      );
      trackEvent(mixpanelEvents.pushToPMSCompleted, {
        status: 'success',
        unifiedId,
        estimatedVisitId,
        txCode: pushTreatmentCodesRef.current,
      });
    } else if (writebackStatus?.status === 'error') {
      hasPushPendingRef.current = false;
      toast(
        <Toast
          variant="error"
          title={t`Error`}
          description={t`Failed to push to PMS. Some documents may not have been uploaded.`}
        />,
        TOAST_TOP_RIGHT,
      );
      trackEvent(mixpanelEvents.pushToPMSCompleted, {
        status: 'error',
        unifiedId,
        estimatedVisitId,
        txCode: pushTreatmentCodesRef.current,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent/t are called on status change, not re-run triggers; hasPushPendingRef is a stable ref
  }, [writebackStatus?.status, unifiedId, estimatedVisitId, isPushingToPMS]);

  // Show timeout toast if push stays pending for > 2 minutes.
  // pushSessionId increments on each push, restarting the timer.
  useEffect(() => {
    if (!hasPushPendingRef.current) return;
    const timer = setTimeout(() => {
      if (hasPushPendingRef.current) {
        hasPushPendingRef.current = false;
        toast(
          <Toast
            variant="error"
            title={t`Push to PMS timed out`}
            description={t`The push may still complete. Check your PMS or try again.`}
          />,
          TOAST_TOP_RIGHT,
        );
      }
    }, 130_000); // 130s — slightly after hook stops polling at ~120s
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pushSessionId is the restart trigger; hasPushPendingRef is a stable ref; t is read inside the callback, not a re-run trigger
  }, [pushSessionId]);

  // Sync loading state with the hook
  useEffect(() => {
    if ((selectedTreatmentIds?.length ?? 0) > 0) {
      setIsNarrativeLoading(isLoadingNarrative || isFetchingNarrative);
    }
  }, [
    isLoadingNarrative,
    selectedTreatmentIds,
    isFetchingNarrative,
    setIsNarrativeLoading,
  ]);

  // Handle treatments and claim notes updates
  useEffect(() => {
    // Set selected treatments when data loads
    if (claimSummaryTreatments?.length && !selectedTreatmentIds?.length) {
      const txIds = claimSummaryTreatments.map((tx) => tx.txId.toString());
      setSelectedTreatmentIds(txIds);
      setSelectedTreatmentCodes([
        ...new Set(claimSummaryTreatments.map((tx) => tx.txCode)),
      ]);
    }

    // Update claim notes and warnings from messages (v7 claim summary + v3 narrative); gate narrative by preference.
    if (
      claimSummary &&
      selectedTreatmentIds &&
      selectedTreatmentIds.length > 0
    ) {
      const { notes, warnings } = getClaimNotesAndWarnings({
        claimSummary,
        selectedTreatmentIds,
        narrativeData,
        showNarrativeMessages: preferences?.narrativeMessagesVisible === true,
      });
      setClaimNotes(notes);
      setClaimWarnings(warnings);
    }

    // Update content height when content changes
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [
    claimSummaryTreatments,
    selectedTreatmentIds,
    claimSummary,
    narrativeData,
    preferences?.narrativeMessagesVisible,
    setSelectedTreatmentIds,
    setSelectedTreatmentCodes,
    setClaimNotes,
    setClaimWarnings,
    contentRef,
    shouldExcludeFmxFindingsWhenPeriochartPresent,
  ]);

  // Update content height when scale or content changes
  useEffect(() => {
    if (contentRef.current && scale <= 1) {
      const element = componentRef.current as HTMLDivElement | null;
      if (element) {
        const height = element.scrollHeight * scale;
        setContentHeight(height);
      }
    }
  }, [
    scale,
    contentRef,
    componentRef,
    selectedTreatmentIds,
    claimSummary,
    shouldExcludeFmxFindingsWhenPeriochartPresent,
  ]);

  const backLinkLabel = hideHuddle ? (
    <Trans>All Patients</Trans>
  ) : (
    <Trans>Dashboard</Trans>
  );

  const isPushLoading =
    isPushingToPMS ||
    writebackStatus?.status === 'pending' ||
    writebackStatus?.status === 'in_progress';
  const isPushSuccess = !isPushLoading && writebackStatus?.status === 'success';
  const isPushError = !isPushLoading && writebackStatus?.status === 'error';

  const lastPushedLabel =
    (isPushSuccess || isPushError) && writebackStatus?.pushedAt
      ? createLocalDate(writebackStatus.pushedAt).toLocaleDateString()
      : null;

  const pushStatusLabel = (
    <PushStatusLabel
      isPushLoading={isPushLoading}
      isPushSuccess={isPushSuccess}
      isPushError={isPushError}
      lastPushedLabel={lastPushedLabel}
      variant={batchSession ? 'batch' : 'default'}
    />
  );

  const headerRightContent =
    insPage !== 'dev-tool' ? (
      <div className="vai-flex vai-gap-2 vai-items-center">
        {isClaimAttachmentEnabled && shouldShowPushToPMS && (
          <>
            {!batchSession && pushStatusLabel}
            <Button
              disabled={isActionsDisabled}
              variant="default"
              onClick={handlePushToPMS}
            >
              {isPushLoading ? (
                <Trans>Pushing to PMS</Trans>
              ) : (
                <Trans>Push to PMS</Trans>
              )}
              {isPushLoading && (
                <FontAwesomeIcon icon={faSpinner} spin className="vai-ml-2" />
              )}
            </Button>
          </>
        )}
        {isClaimAttachmentEnabled && (
          <Button
            disabled={isActionsDisabled}
            loading={isDownloadingPieces}
            title={t`Download`}
            onClick={handleDownloadPieces}
            variant="outline"
          >
            <FontAwesomeIcon
              className="vai-text-[#18181B]"
              icon={faArrowDownToLine}
            />
          </Button>
        )}
        {isClaimAttachmentEnabled && (
          <Button
            disabled={isActionsDisabled}
            loading={isPdfLoading}
            title={t`Print Version`}
            variant="outline"
            onClick={() => {
              handleGeneratePdf();
            }}
          >
            <FontAwesomeIcon className="vai-text-[#18181B]" icon={faPrint} />
          </Button>
        )}
      </div>
    ) : null;

  // Safety redirect — should not happen given the route guarantees :unifiedId
  if (!unifiedIdParam) {
    return <Redirect to={routesConstants.CLAIMS} />;
  }

  return (
    <div className="vai-w-screen vai-overflow-hidden vai-flex vai-flex-col vai-h-full">
      {insPage === 'dev-tool' ? (
        <div
          className="vai-flex vai-min-h-[65px] vai-px-6 vai-py-3 vai-bg-white vai-border-b vai-border-gray-200 vai-text-primary vai-justify-between vai-items-center"
          style={{ flexShrink: 0 }}
        >
          <Button
            onClick={() => setInsPage('preview-single')}
            variant="ghost"
            className="vai-inline-flex vai-items-center vai-text-primary vai-p-0 vai-h-auto hover:vai-bg-transparent"
          >
            <FontAwesomeIcon
              icon={faChevronLeft}
              className="vai-mr-2 vai-text-primary"
            />
            <Trans>Back to Claim Attachment</Trans>
          </Button>
        </div>
      ) : batchSession ? (
        <BatchHeader
          session={batchSession}
          leftContent={pushStatusLabel}
          rightContent={headerRightContent}
        />
      ) : (
        <BackLinkSubheader
          to={routesConstants.CLAIMS}
          onClick={handleBackLinkClick}
          rightContent={headerRightContent}
        >
          {backLinkLabel}
        </BackLinkSubheader>
      )}
      {isLoadingClaimSummary ? (
        <LoadingAnimation translatedMessages={loadingMessages} />
      ) : (
        <div className="vai-flex-1 vai-overflow-auto">
          {insPage === 'dev-tool' ? (
            <NarrativeDevTool />
          ) : (
            <div className="vai-flex vai-flex-row vai-justify-between vai-h-full vai-gap-4">
              <div className="vai-flex-1 vai-h-full vai-overflow-hidden">
                <div className="vai-flex vai-flex-col vai-h-full">
                  <div className="vai-sticky vai-z-10 vai-w-full vai-flex vai-items-center vai-justify-between vai-gap-4 vai-px-4">
                    {/* Left: Rate this attachment */}
                    <div className="vai-flex vai-items-center vai-shrink-0">
                      {preferences?.isFeedbackWidgetEnabled && (
                        <FeedbackWidget
                          onRatingButtonClick={handleAttachmentButtonClick}
                          onRate={handleSubmitAttachmentFeedback}
                          label={<Trans>Rate this attachment</Trans>}
                          upOptions={RATE_ATTACHMENT_OPTIONS_UP}
                          downOptions={RATE_ATTACHMENT_OPTIONS_DOWN}
                        />
                      )}
                    </div>
                    {/* Center: Zoom Tool Topbar */}
                    <div className="vai-flex-1 vai-flex vai-justify-center vai-min-w-0">
                      <ZoomToolTopbar
                        contentRef={contentRef}
                        componentRef={
                          componentRef as React.RefObject<HTMLDivElement>
                        }
                        scale={scale}
                        selectedTreatmentIds={selectedTreatmentIds}
                        claimSummary={claimSummary}
                        hasInitiallyScaled={hasInitiallyScaled}
                        onSetHasInitiallyScaled={setHasInitiallyScaled}
                        onSetScale={setScale}
                      />
                    </div>
                    {/* Right: Select images */}
                    <div className="vai-flex vai-items-center vai-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          trackEvent(mixpanelEvents.claimSelectImages, {
                            unifiedId,
                            estimatedVisitId,
                          });
                          setIsImageSelectorOpen(true);
                        }}
                        disabled={
                          !filteredColorImages?.length &&
                          !filteredRadiographs?.length &&
                          !hasQuadLevelSummary
                        }
                      >
                        <FontAwesomeIcon
                          icon={faImages}
                          className="vai-h-4 vai-w-4 vai-text-muted-foreground"
                        />
                        <Trans>Select Images</Trans>
                      </Button>
                    </div>
                  </div>
                  {/* Content Container */}
                  <div className="vai-flex-1 vai-w-full vai-overflow-auto vai-flex vai-justify-center">
                    <div
                      ref={contentRef}
                      style={{
                        transform: `scale(${scale})`,
                        transformOrigin: 'top center',
                        height: `${contentHeight}px`,
                      }}
                    >
                      <div
                        className="vai-p-4 vai-pt-0 vai-min-w-fit"
                        ref={componentRef as React.RefObject<HTMLDivElement>}
                      >
                        <ClaimAttachmentContent
                          claimSummary={claimSummary}
                          isLoadingClaimSummary={isLoadingClaimSummary}
                          hasToothLevelSummary={hasToothLevelSummary}
                          hasQuadLevelSummary={hasQuadLevelSummary && showFMX}
                          filteredColorImages={visibleColorImages}
                          filteredRadiographs={visibleRadiographs}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Claim Customization Sidebar */}
              <ClaimCustomizationSidebar
                defaultTreatments={claimSummaryTreatments}
                onSelectTreatments={handleSelectTreatments}
                claimSummary={claimSummary}
              />
            </div>
          )}
        </div>
      )}
      {/* Image selection modal */}
      {isImageSelectorOpen && (
        <ClaimImageSelector
          claimSummary={claimSummary}
          filteredColorImages={filteredColorImages}
          filteredRadiographs={filteredRadiographs}
          selectedTreatmentIds={selectedTreatmentIds || []}
          hasQuadLevelSummary={hasQuadLevelSummary}
          showFMX={showFMX}
          onSetImageSelectorOpen={setIsImageSelectorOpen}
          onSetShowFMX={setShowFMX}
        />
      )}

      <Toaster />
    </div>
  );
};

// Export the wrapper component instead to include context provider
export { ClaimSummaryPageWrapper as ClaimSummaryPage };
